import React, { useCallback, useEffect, useState } from "react";
import { Button, FormGroup, Input, Label, Modal, ModalBody, Spinner, Table } from "reactstrap";

import { detalheNotaResultado, notasResultado, resumoBaseline } from "services/bidFreteApi";
import TablePagination from "../../../../../components/TablePagination";
import NotaFiscalDetailModal from "../../../ConsultaNotasFiscais/NotaFiscalDetailModal";
import { FONTE_UI, erroDaApi, fmtBRL, fmtDataISO, fmtNumero, fmtPct, listaSiglas, nomeCandidata } from "../regras";

const STATUS_NF = [
  { value: "", label: "Todas" },
  { value: "COMPARAVEL", label: "Comparáveis (com CTe)" },
  { value: "SEM_CTE", label: "Sem CTe" },
  { value: "SEM_CALCULO", label: "Sem cálculo" },
  { value: "ERRO", label: "Com erro" },
];
const FAIXAS = [
  { value: "", label: "Todas" },
  { value: "negativa", label: "Mais caro que o cobrado" },
  { value: "0-5", label: "0 a 5%" },
  { value: "5-15", label: "5 a 15%" },
  { value: "15+", label: "15% ou mais" },
];

// O detalhe vem da instância do BID num formato próprio: lista as linhas com
// o que der para ler e mostra o resto como texto.
function linhasDoDetalhe(detalhe) {
  if (!detalhe) return [];
  if (Array.isArray(detalhe)) return detalhe;
  return detalhe.itens || detalhe.resultados || detalhe.items || [];
}

// Aba Notas (H3 PO1-2844): matriz paginada no servidor (molde da Conferência
// de Faturas) com o cobrado e o custo extra da baseline e as 3 melhores
// candidatas por NF; filtros de UF, transportadora atual, só comparáveis e
// busca. Clicar na linha abre o detalhe fiscal (GET /resultado/notas/:nf/detalhe,
// recalculado sob demanda na instância do BID, sem gravar) com atalho para a NF.

const PAGE_SIZES = [10, 20, 50, 100];

function DetalheNotaModal({ isOpen, onClose, empresaId, bid, nota, onAbrirNf }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!isOpen || !nota) return undefined;
    let ativo = true;
    setLoading(true);
    setErro(null);
    setDados(null);
    detalheNotaResultado(empresaId, bid.id, nota.idNotaFiscal)
      .then((r) => {
        if (ativo) setDados(r);
      })
      .catch((err) => {
        if (!ativo) return;
        setErro(erroDaApi(err, "Não foi possível carregar o detalhe fiscal.").mensagem);
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [isOpen, nota, empresaId, bid]);

  // { nota: { ..., candidatas }, detalhe, motivo? }: as candidatas gravadas na
  // simulação + o recálculo da instância do BID.
  const candidatas = (dados && dados.nota && dados.nota.candidatas) || (dados && dados.candidatas) || [];
  const recalculo = linhasDoDetalhe(dados && dados.detalhe);
  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" modalClassName="modal-align-top" contentClassName="lf-modal" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>NF {nota ? nota.numero || nota.idNotaFiscal : ""} · detalhe fiscal</span>
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>
      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {nota ? (
          <div className="lf-kv" style={{ marginBottom: 12 }}>
            <div>
              <div className="lf-kv-label">Emissão</div>
              <div className="lf-kv-value">{fmtDataISO(nota.dataEmissao)}</div>
            </div>
            <div>
              <div className="lf-kv-label">Destino</div>
              <div className="lf-kv-value">{[nota.cidadeDestino, nota.ufDestino].filter(Boolean).join("/") || "—"}</div>
            </div>
            <div>
              <div className="lf-kv-label">Cobrado (baseline)</div>
              <div className="lf-kv-value">{nota.comparavel === false || nota.comparavel === 0 ? "sem CTe" : fmtBRL(Number(nota.vrFrete))}</div>
            </div>
            <div>
              <div className="lf-kv-label">Custo extra</div>
              <div className="lf-kv-value">{fmtBRL(Number(nota.vrComplementar) || 0)}</div>
            </div>
            <div>
              <div className="lf-kv-label">Transportadora atual</div>
              <div className="lf-kv-value">{nota.transportadoraAtual || "—"}</div>
            </div>
          </div>
        ) : null}
        {loading ? (
          <div className="lf-empty-state">
            <Spinner size="sm" /> Recalculando as candidatas...
          </div>
        ) : erro ? (
          <div className="lf-aviso lf-aviso-danger">{erro}</div>
        ) : candidatas.length === 0 ? (
          <div className="lf-empty-state">Sem cálculo para esta NF.</div>
        ) : (
          <Table className="bf-table" size="sm" responsive>
            <thead className="text-primary">
              <tr>
                <th>Transportadora</th>
                <th>Fonte</th>
                <th>Status</th>
                <th className="is-number">Frete</th>
                <th className="is-number">Imposto</th>
                <th className="is-number">Prazo</th>
              </tr>
            </thead>
            <tbody>
              {candidatas.map((c, i) => {
                const fonte = FONTE_UI[c.fonte] || FONTE_UI.TABELA;
                return (
                  <tr key={`${c.idTransportadora}-${c.fonte}-${i}`}>
                    <td>
                      {nomeCandidata(c)}
                      {c.tabela || c.idFreteTabela ? <div className="lf-muted">{c.tabela || `tabela ${c.idFreteTabela}`}</div> : null}
                    </td>
                    <td>
                      <span className={`lf-badge lf-badge-${fonte.tone}`}>{fonte.label}</span>
                    </td>
                    <td>
                      {c.status || "—"}
                      {c.motivo ? <div className="lf-muted">{c.motivo}</div> : null}
                    </td>
                    <td className="is-number">{c.vrFrete != null ? fmtBRL(Number(c.vrFrete)) : "—"}</td>
                    <td className="is-number">{c.vrImposto != null ? fmtBRL(Number(c.vrImposto)) : "—"}</td>
                    <td className="is-number">{c.diasPrazo != null ? `${c.diasPrazo} d` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {dados && dados.motivo === "sem_candidatas_de_tabela" ? <div className="lf-muted">Só há candidatas por conector nesta NF: o recálculo fiscal vale só para tabela e proposta.</div> : null}
        {recalculo.length ? (
          <details style={{ marginTop: 10 }}>
            <summary className="lf-muted">Recálculo fiscal da instância do BID ({recalculo.length} item(ns))</summary>
            <pre style={{ fontSize: 11, maxHeight: 260, overflow: "auto", color: "var(--everlog-card-text)" }}>{JSON.stringify(recalculo, null, 2)}</pre>
          </details>
        ) : null}
        {nota ? (
          <div className="text-right" style={{ marginTop: 10 }}>
            <Button color="info" size="sm" outline onClick={() => onAbrirNf(nota.idNotaFiscal)}>
              Abrir a NF
            </Button>
          </div>
        ) : null}
      </ModalBody>
    </Modal>
  );
}

export default function AbaNotas({ bid, empresaId }) {
  const simulada = Boolean(bid && (bid.etapas || []).some((e) => e.etapa === "SIMULACAO" && e.status === "CONCLUIDA"));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filtros, setFiltros] = useState({ uf: "", idTransportadora: "", status: "", faixaEconomia: "", busca: "" });
  const [transportadoras, setTransportadoras] = useState([]);
  const [aplicados, setAplicados] = useState(filtros);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [notaAberta, setNotaAberta] = useState(null);
  const [nfAbertaId, setNfAbertaId] = useState(null);

  const carregar = useCallback(async () => {
    if (!simulada || !empresaId || !bid) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await notasResultado(empresaId, bid.id, { page, pageSize, ...aplicados });
      setDados(r);
    } catch (err) {
      setErro(erroDaApi(err, "Não foi possível carregar as notas.").mensagem);
    } finally {
      setLoading(false);
    }
  }, [simulada, empresaId, bid, page, pageSize, aplicados]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Transportadoras atuais do filtro: a quebra da baseline.
  useEffect(() => {
    if (!simulada || !empresaId || !bid) return undefined;
    let ativo = true;
    resumoBaseline(empresaId, bid.id)
      .then((r) => {
        if (ativo) setTransportadoras((r && r.porTransportadora) || []);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [simulada, empresaId, bid]);

  if (!simulada) return <div className="bf-placeholder">As notas aparecem depois da simulação.</div>;

  const itens = (dados && (dados.items || dados.itens)) || [];
  const total = (dados && (dados.totalCount != null ? dados.totalCount : dados.total)) || 0;
  const ufs = listaSiglas(bid.ufs);
  const mudar = (patch) => setFiltros((f) => ({ ...f, ...patch }));

  return (
    <>
      <div className="bf-tab-toolbar">
        <FormGroup>
          <Label>Busca</Label>
          <Input
            type="text"
            bsSize="sm"
            placeholder="Nº da NF ou cidade"
            value={filtros.busca}
            onChange={(e) => {
              const v = e.target.value;
              mudar({ busca: v });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setAplicados(filtros);
                setPage(1);
              }
            }}
          />
        </FormGroup>
        <FormGroup>
          <Label>UF</Label>
          <Input
            type="select"
            bsSize="sm"
            value={filtros.uf}
            onChange={(e) => {
              const v = e.target.value;
              mudar({ uf: v });
            }}
          >
            <option value="">Todas</option>
            {ufs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </Input>
        </FormGroup>
        {transportadoras.length ? (
          <FormGroup>
            <Label>Transportadora atual</Label>
            <Input
              type="select"
              bsSize="sm"
              value={filtros.idTransportadora}
              onChange={(e) => {
                const v = e.target.value;
                mudar({ idTransportadora: v });
              }}
            >
              <option value="">Todas</option>
              {transportadoras.map((t) => (
                <option key={t.idTransportadora} value={t.idTransportadora}>
                  {t.nome}
                </option>
              ))}
            </Input>
          </FormGroup>
        ) : null}
        <FormGroup>
          <Label>Situação</Label>
          <Input
            type="select"
            bsSize="sm"
            value={filtros.status}
            onChange={(e) => {
              const v = e.target.value;
              mudar({ status: v });
            }}
          >
            {STATUS_NF.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label>Economia</Label>
          <Input
            type="select"
            bsSize="sm"
            value={filtros.faixaEconomia}
            onChange={(e) => {
              const v = e.target.value;
              mudar({ faixaEconomia: v });
            }}
          >
            {FAIXAS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Input>
        </FormGroup>
        <Button
          color="primary"
          size="sm"
          onClick={() => {
            setAplicados(filtros);
            setPage(1);
          }}
          disabled={loading}
        >
          Aplicar
        </Button>
      </div>

      {erro ? <div className="lf-aviso lf-aviso-danger">{erro}</div> : null}
      {loading && !dados ? (
        <div className="lf-empty-state">
          <Spinner size="sm" /> Carregando as notas...
        </div>
      ) : itens.length === 0 ? (
        <div className="lf-empty-state">Nenhuma nota para os filtros.</div>
      ) : (
        <Table className="bf-table" size="sm" responsive hover>
          <thead className="text-primary">
            <tr>
              <th>NF</th>
              <th>Emissão</th>
              <th>Destino</th>
              <th>Transp. atual</th>
              <th className="is-number">Peso</th>
              <th className="is-number">Cobrado</th>
              <th className="is-number">Extra</th>
              <th className="is-number">Melhor</th>
              <th className="is-number">Economia</th>
              <th>Melhores candidatas</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((n) => {
              const comparavel = !(n.comparavel === false || n.comparavel === 0);
              return (
                <tr key={n.idNotaFiscal} className="is-clicavel" onClick={() => setNotaAberta(n)}>
                  <td>{n.numero || n.idNotaFiscal}</td>
                  <td>{fmtDataISO(n.dataEmissao)}</td>
                  <td>{[n.cidadeDestino, n.ufDestino].filter(Boolean).join("/") || "—"}</td>
                  <td>{n.transportadoraAtual || "—"}</td>
                  <td className="is-number">{n.pesoBruto != null ? `${fmtNumero(n.pesoBruto, 1)} kg` : "—"}</td>
                  <td className="is-number">{comparavel ? fmtBRL(Number(n.vrFrete)) : <span className="lf-muted">sem CTe</span>}</td>
                  <td className="is-number">{Number(n.vrComplementar) > 0 ? fmtBRL(Number(n.vrComplementar)) : "—"}</td>
                  <td className="is-number">{n.melhorValor != null ? fmtBRL(Number(n.melhorValor)) : "—"}</td>
                  <td className={`is-number ${Number(n.economia) < 0 ? "bf-text-danger" : "bf-text-success"}`}>
                    {comparavel && n.economia != null ? fmtBRL(Number(n.economia)) : "—"}
                    {comparavel && n.economiaPct != null ? <div className="lf-muted">{fmtPct(Number(n.economiaPct))}</div> : null}
                  </td>
                  <td>
                    <div className="bf-top3">
                      {(n.melhores || []).slice(0, 3).map((m, i) => (
                        <div key={`${m.idTransportadora}-${i}`}>
                          <span className="bf-top3-valor">{fmtBRL(Number(m.vrFrete))}</span> <span className="bf-top3-nome">{nomeCandidata(m)}</span>
                          {m.diasPrazo != null ? <span className="lf-muted"> · {m.diasPrazo} d</span> : null}
                        </div>
                      ))}
                      {!(n.melhores || []).length ? <span className="lf-muted">—</span> : null}
                      {Number(n.qtdErro) > 0 || Number(n.qtdNaoCalculada) > 0 ? (
                        <span className="bf-text-warning" style={{ fontSize: 11 }}>
                          {Number(n.qtdNaoCalculada) > 0 ? `${n.qtdNaoCalculada} sem cálculo` : ""}
                          {Number(n.qtdNaoCalculada) > 0 && Number(n.qtdErro) > 0 ? " · " : ""}
                          {Number(n.qtdErro) > 0 ? `${n.qtdErro} com erro` : ""}
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {total > 0 ? (
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          pageSizes={PAGE_SIZES}
          itemLabel="nota(s)"
          disabled={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      ) : null}

      <DetalheNotaModal
        isOpen={Boolean(notaAberta)}
        onClose={() => setNotaAberta(null)}
        empresaId={empresaId}
        bid={bid}
        nota={notaAberta}
        onAbrirNf={(id) => {
          setNotaAberta(null);
          setNfAbertaId(id);
        }}
      />
      <NotaFiscalDetailModal isOpen={Boolean(nfAbertaId)} notaFiscalId={nfAbertaId} embarcadorId={empresaId} onClose={() => setNfAbertaId(null)} />
    </>
  );
}
